import React, { useState, useEffect, useMemo } from "react";
import { lib } from "../index";
import SearchList from "../config-center-v3/search-list";
import { Button, message, Space, Modal, Form, Input, Select, Typography, Divider, Alert } from "antd";
import "./account.less";
import HOC from "../util/HOC";
import { env } from "@dt/util";
import axios from "axios";

const { Title } = Typography;
const { Option } = Select;

// 新增/编辑部门组件
function CreateOrUpdateDept({ onFinish, item }) {
    const [visible, setVisible] = useState(false);
    const [form] = Form.useForm();
    const [deptList, setDeptList] = useState([]);
    const [userList, setUserList] = useState([]);

    // 获取部门列表用于选择上级部门
    const fetchDeptList = () => {
        lib.request({
            url: "/ucenter-admin/dept/tree",
            data: { systemCode: lib.config.systemCode },
            success: data => {
                console.log("deptList", data);

                setDeptList(data || []);
            },
            fail: () => {
                message.error("获取部门列表失败");
                setDeptList([]);
            },
        });
    };

    // 获取用户列表用于选择部门主管
    const fetchUserList = () => {
        const requestUrl =
            lib.config.webToken === "admin" ? "/ucenter-admin/waiter/page" : "/ucenter-customer/user/sub/page";

        lib.request({
            url: requestUrl,
            data: {
                systemCode: lib.config.systemCode,
                currentPage: 1,
                pageSize: 1000, // 获取足够多的用户数据
                status: 1, // 只获取启用状态的用户
            },
            success: data => {
                setUserList(data.dataList || []);
            },
            fail: () => {
                message.error("获取用户列表失败");
                setUserList([]);
            },
        });
    };

    useEffect(() => {
        if (visible) {
            fetchDeptList();
            fetchUserList();
            if (item) {
                form.setFieldsValue({
                    name: item.name,
                    code: item.code,
                    parentId: item.parentId,
                    managerId: item.managerId,
                    remark: item.remark,
                    status: item.status,
                });
            } else {
                form.resetFields();
            }
        }
    }, [visible, item]);

    const handleSubmit = () => {
        form.validateFields()
            .then(values => {
                const url = item ? "/ucenter-admin/dept/update" : "/ucenter-admin/dept/add";
                const data = {
                    ...values,
                    systemCode: lib.config.systemCode,
                };
                if (item) {
                    data.id = item.id;
                }

                lib.request({
                    url,
                    data,
                    needMask: true,
                    success: () => {
                        message.success(item ? "修改成功" : "新增成功");
                        form.resetFields();
                        setVisible(false);
                        onFinish && onFinish();
                    },
                    fail: error => {
                        // 处理常见的错误情况
                        if (error && error.message) {
                            if (error.message.includes("编码") && error.message.includes("重复")) {
                                message.error("部门编码已存在，请使用其他编码");
                            } else if (error.message.includes("名称") && error.message.includes("重复")) {
                                message.error("部门名称已存在，请使用其他名称");
                            } else {
                                message.error(error.message);
                            }
                        } else {
                            message.error(item ? "修改失败" : "新增失败");
                        }
                    },
                });
            })
            .catch(info => {
                console.log("Validate Failed:", info);
                message.warning("请检查表单填写是否正确");
            });
    };

    // 处理取消操作
    const handleCancel = () => {
        const hasChanges = form.isFieldsTouched();
        if (hasChanges) {
            Modal.confirm({
                title: "确认取消",
                content: "表单内容尚未保存，确定要取消吗？",
                onOk: () => {
                    form.resetFields();
                    setVisible(false);
                },
            });
        } else {
            setVisible(false);
        }
    };

    // 递归渲染部门选项
    const renderDeptOptions = (depts, level = 0) => {
        return depts
            .map(dept => {
                const prefix = "　".repeat(level);
                // 禁用自己和自己的子部门，防止循环引用
                const isDisabled = item && (dept.id === item.id || isChildDept(dept, item.id));
                return [
                    <Option key={dept.id} value={dept.id} disabled={isDisabled}>
                        {prefix}
                        {dept.name}
                    </Option>,
                    ...(dept.children ? renderDeptOptions(dept.children, level + 1) : []),
                ];
            })
            .flat();
    };

    // 检查是否为子部门（递归检查）
    const isChildDept = (dept, parentId) => {
        if (dept.parentId === parentId) return true;
        if (dept.children) {
            return dept.children.some(child => isChildDept(child, parentId));
        }
        return false;
    };

    return (
        <>
            {visible && (
                <Modal
                    open={true}
                    title={item ? "编辑部门" : "新增部门"}
                    width={900}
                    className="dept-form-modal"
                    maskClosable={false}
                    onCancel={handleCancel}
                    onOk={handleSubmit}>
                    <Form form={form} layout="horizontal" name="dept_form">
                        <Typography>
                            {/* 第一行：部门名称 + 部门编码 */}
                            <div style={{ display: "flex", gap: "16px" }}>
                                <Form.Item
                                    name="name"
                                    label="部门名称"
                                    labelCol={{ span: 8 }}
                                    wrapperCol={{ span: 16 }}
                                    style={{ flex: 1 }}
                                    rules={[
                                        { required: true, message: "请输入部门名称" },
                                        { max: 32, message: "部门名称不能超过32个字符" },
                                    ]}>
                                    <Input placeholder="请输入部门名称" disabled={!!item} />
                                </Form.Item>
                                <Form.Item
                                    name="code"
                                    label="部门编码"
                                    labelCol={{ span: 8 }}
                                    wrapperCol={{ span: 16 }}
                                    style={{ flex: 1 }}
                                    rules={[
                                        { required: true, message: "请输入部门编码" },
                                        { max: 32, message: "部门编码不能超过32个字符" },
                                        {
                                            pattern: /^[a-zA-Z0-9_-]+$/,
                                            message: "部门编码只能包含字母、数字、下划线和横线",
                                        },
                                    ]}>
                                    <Input placeholder="请输入部门编码（字母、数字、下划线、横线）" disabled={!!item} />
                                </Form.Item>
                            </div>

                            {/* 第二行：上级部门 + 状态 */}
                            <div style={{ display: "flex", gap: "16px" }}>
                                <Form.Item
                                    name="parentId"
                                    label="上级部门"
                                    labelCol={{ span: 8 }}
                                    wrapperCol={{ span: 16 }}
                                    style={{ flex: 1 }}>
                                    <Select
                                        placeholder="请选择上级部门"
                                        allowClear
                                        showSearch
                                        disabled={!!item}
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }>
                                        {renderDeptOptions(deptList)}
                                    </Select>
                                </Form.Item>
                                <Form.Item
                                    name="status"
                                    label="状态"
                                    labelCol={{ span: 8 }}
                                    wrapperCol={{ span: 16 }}
                                    style={{ flex: 1 }}
                                    initialValue={1}
                                    rules={[{ required: true, message: "请选择状态" }]}>
                                    <Select placeholder="请选择状态">
                                        <Option value={1}>启用</Option>
                                        <Option value={0}>禁用</Option>
                                    </Select>
                                </Form.Item>
                            </div>

                            {/* 第三行：部门主管 + 空位 */}
                            <div style={{ display: "flex", gap: "16px" }}>
                                <Form.Item
                                    name="managerId"
                                    label="部门主管"
                                    labelCol={{ span: 8 }}
                                    wrapperCol={{ span: 16 }}
                                    style={{ flex: 1 }}>
                                    <Select
                                        placeholder="请选择部门主管"
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }>
                                        {userList.map(user => (
                                            <Option key={user.id} value={user.id}>
                                                {user.nickName}({user.userName})
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <div style={{ flex: 1 }}></div>
                            </div>

                            {/* 第四行：备注（占满整行） */}
                            <Form.Item name="remark" label="备注" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
                                <Input.TextArea rows={3} placeholder="请输入备注信息" maxLength={500} />
                            </Form.Item>
                        </Typography>
                    </Form>
                </Modal>
            )}
            {item ? (
                <div className="link" onClick={() => setVisible(true)}>
                    编辑
                </div>
            ) : (
                <Button type="primary" onClick={() => setVisible(true)}>
                    新增部门
                </Button>
            )}
        </>
    );
}

@HOC.mapAuthButtonsToState({ pagePath: "/organizational/dept-manage" })
class DeptManage extends SearchList {
    constructor(props) {
        super(props);
        this.state.buttons = [];
        this.allDeptData = []; // 存储所有部门数据用于前端搜索
        this._isMounted = true; // 初始化时设为true，因为组件正在构造中
    }

    componentWillUnmount() {
        this._isMounted = false;
        super.componentWillUnmount();
    }

    // 配置加载完成后的回调，在这里调用load方法
    setConfigSuccess() {
        this.load();
    }

    isContainCode(code) {
        return this.state.buttons.includes(code);
    }

    getConfig() {
        return Promise.resolve({
            page: {
                api: "/ucenter-admin/dept/tree",
                bordered: true,
                onlyOneLevel: true,
            },
            searchKeyList: [
                {
                    id: 1,
                    label: "部门名称：",
                    key: "name",
                    type: "input",
                    extra: "",
                },
                {
                    id: 2,
                    label: "状态：",
                    key: "status",
                    type: "select",
                    source: "json",
                    json: '[{"id":"1","name":"启用"},{"id":"0","name":"禁用"}]',
                    placeholder: "请选择状态",
                },
            ],
            tableFieldList: [
                {
                    id: 1,
                    title: "部门名称",
                    key: "name",
                    width: 200,
                    fixed: "show",
                },
                {
                    id: 2,
                    title: "部门编码",
                    key: "code",
                    width: 150,
                    fixed: "show",
                },
                {
                    id: 3,
                    title: "状态",
                    key: "renderStatus",
                    width: 100,
                    fixed: "show",
                    type: "function",
                },
                {
                    id: 4,
                    title: "备注",
                    key: "remark",
                    width: 200,
                    fixed: "show",
                },
                {
                    id: 6,
                    title: "创建时间",
                    key: "createTime",
                    width: 150,
                    fixed: "show",
                    render: "renderCreateTime",
                },
                {
                    id: 7,
                    title: "更新时间",
                    key: "updateTime",
                    width: 150,
                    fixed: "show",
                    render: "renderUpdateTime",
                },
                {
                    id: 8,
                    title: "操作",
                    key: "renderRowOperation",
                    width: 280,
                    fixed: "right",
                    type: "function",
                },
            ],
        });
    }

    configLoadDefaultParams() {
        return { systemCode: lib.config.systemCode };
    }

    // 重写load方法实现前端搜索
    load(_) {
        let { search, config } = this.state;

        // 如果配置还没有加载完成，直接返回
        if (!config) {
            return;
        }

        const configDefaultParams = this.configLoadDefaultParams();
        this.setState({ _loading: true });

        // 如果是初次加载或者需要刷新数据，从服务器获取所有数据
        if (!this.allDeptData.length || _ === true) {
            lib.request({
                url: config.page.api,
                data: configDefaultParams, // 不传搜索条件，获取所有数据
                success: data => {
                    if (!this._isMounted) return; // 检查组件是否仍然挂载
                    this.allDeptData = data || [];
                    this.filterAndSetData(search);
                },
                fail: () => {
                    if (!this._isMounted) return; // 检查组件是否仍然挂载
                    this.setState({ _loading: false });
                    message.error("获取部门数据失败");
                },
            });
        } else {
            // 使用已有数据进行前端过滤
            this.filterAndSetData(search);
        }
    }

    // 前端数据过滤方法
    filterAndSetData(search) {
        if (!this._isMounted) return; // 如果组件未挂载，直接返回

        let filteredData = [...this.allDeptData];

        // 根据搜索条件过滤数据
        if (search && Object.keys(search).length > 0) {
            filteredData = this.filterDeptData(filteredData, search);
        }

        // 处理树形数据，移除空的 children 数组
        const processTreeData = list => {
            if (!Array.isArray(list)) return list;
            return list.map(item => {
                const newItem = { ...item };
                if (Array.isArray(newItem.children)) {
                    if (newItem.children.length === 0) {
                        delete newItem.children;
                    } else {
                        newItem.children = processTreeData(newItem.children);
                    }
                }
                return newItem;
            });
        };

        const processedData = processTreeData(filteredData);

        this.setState({
            dataList: processedData,
            _loading: false,
        });
    }

    // 递归过滤部门数据
    filterDeptData(data, search) {
        const { name, status } = search;

        const filterNode = node => {
            let shouldInclude = true;

            // 部门名称过滤
            if (name && name.trim()) {
                shouldInclude =
                    shouldInclude && node.name && node.name.toLowerCase().includes(name.toLowerCase().trim());
            }

            // 状态过滤
            if (status !== undefined && status !== "") {
                shouldInclude = shouldInclude && node.status === parseInt(status);
            }

            // 处理子节点
            let filteredChildren = [];
            if (node.children && node.children.length > 0) {
                filteredChildren = node.children.map(filterNode).filter(child => child !== null);
            }

            // 如果当前节点匹配条件，或者有匹配的子节点，则包含此节点
            if (shouldInclude || filteredChildren.length > 0) {
                return {
                    ...node,
                    children: filteredChildren.length > 0 ? filteredChildren : undefined,
                };
            }

            return null;
        };

        return data.map(filterNode).filter(node => node !== null);
    }

    // 重写searchCheck方法，实现前端搜索而不发送请求到后端
    searchCheck(search) {
        // 直接进行前端过滤，不发送请求
        this.filterAndSetData(search);
        this.onSearch && this.onSearch(search);
    }

    reload() {
        this.load(true);
    }

    renderOperationTopView() {
        return null;
    }

    renderLeftOperation() {
        return (
            <Space>
                <CreateOrUpdateDept onFinish={() => this.reload()} />
            </Space>
        );
    }

    renderStatus(row) {
        return (
            <span className={row.status === 1 ? "text-success" : "text-danger"}>
                {row.status === 1 ? "启用" : "禁用"}
            </span>
        );
    }

    renderRowOperation(row) {
        return (
            <Space size={8}>
                {this.isContainCode("update") && <CreateOrUpdateDept onFinish={() => this.reload()} item={row} />}
                <span className="link" onClick={() => this.openUserManage(row)}>
                    绑定用户
                </span>
                {this.isContainCode("log") && (
                    <span className="link" onClick={() => this.openLogManage(row)}>
                        日志
                    </span>
                )}
            </Space>
        );
    }

    openUserManage(row) {
        lib.openPage(
            `/dept-add-user?departmentId=${row.id}&deptName=${encodeURIComponent(row.name)}&page_title=部门人员管理`,
        );
    }

    openLogManage(row) {
        lib.openPage(`/dept-log?departmentId=${row.id}&deptName=${encodeURIComponent(row.name)}&page_title=部门日志`);
    }

    deleteHandle(row) {
        Modal.confirm({
            title: "确认删除",
            content: `确定要删除部门"${row.name}"吗？`,
            onOk: () => {
                lib.request({
                    url: "/ucenter-admin/dept/delete",
                    data: {
                        id: row.id,
                        systemCode: lib.config.systemCode,
                    },
                    needMask: true,
                    success: () => {
                        message.success("删除成功");
                        this.reload();
                    },
                });
            },
        });
    }

    renderCreateTime(row) {
        return row.createTime || "-";
    }

    renderUpdateTime(row) {
        return row.updateTime || "-";
    }
}

export { CreateOrUpdateDept };

export default props => {
    // 确保不传递可能导致额外按钮的 props
    const { renderLeftOperation, renderRightOperation, ...cleanProps } = props;
    return <DeptManage {...cleanProps} name="dept-manage" />;
};
